import { useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import { Add, Cached, Clear, Search } from "@mui/icons-material";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import WorkspacesOutlinedIcon from "@mui/icons-material/WorkspacesOutlined";
import CloudSyncOutlinedIcon from "@mui/icons-material/CloudSyncOutlined";
import TopBarFilter from "../../../components/topBarFilter";
import VirtualizedAutocomplete from "../../../components/virtualization/virtualizedAutocomplete";
import { maskCpfCnpj, tipoDocumento } from "../../../utils/masks";
import TipoClienteFilter from "../../../components/tipoCliente/TipoClienteFilter";
import {
  getClienteOptionLabel,
  getNomeCliente,
} from "../../../components/grupoEconomico/helpers";
import {
  CampoCliente,
  SeloCliente,
  renderOpcaoCliente,
} from "../../../components/opcaoCliente";

const enableClientCreationModal =
  (
    window?.config?.REACT_APP_ENABLE_CLIENT_CREATION_MODAL ||
    process.env.REACT_APP_ENABLE_CLIENT_CREATION_MODAL
  )?.toLowerCase() === "true";

// Rótulo dos autocompletes que buscam o cliente no ERP por IPV4 ou por login.
// Precisa ser exatamente o mesmo no getOptionLabel e no CampoCliente: é a
// comparação entre o rótulo e o texto do input que diz se o usuário já começou
// a digitar, e é isso que mantém as reticências do nome longo.
const getClienteErpOptionLabel = (option) =>
  option
    ? `${option.clienteErpId || ""} - ${getNomeCliente(option)} `
    : "";

const getServiceId = (service) =>
  service?.id_cliente_servico ??
  service?.id ??
  service?.refLabel?.id_cliente_servico ??
  service?.refLabel?.id;

function ServiceFilter({
  styles,
  label,
  value,
  options,
  onChange,
  getServiceLabel,
  filterOptions,
  renderOption,
}) {
  return (
    <Autocomplete
      size="small"
      value={value}
      options={options || []}
      sx={styles.searchField}
      getOptionLabel={getServiceLabel}
      isOptionEqualToValue={(option, currentValue) => {
        if (!currentValue) return false;

        if (typeof currentValue === "string") {
          return getServiceLabel(option) === currentValue;
        }

        const optionId = getServiceId(option);
        const currentValueId = getServiceId(currentValue);

        if (
          optionId === undefined ||
          optionId === null ||
          currentValueId === undefined ||
          currentValueId === null
        ) {
          return false;
        }

        return String(optionId) === String(currentValueId);
      }}
      onChange={onChange}
      renderInput={(params) => <TextField {...params} label={label} />}
      filterOptions={filterOptions}
      renderOption={renderOption}
    />
  );
}

export default function PerfilFilter({
  actionsPermission,
  admPermissao,
  allHostsCliente,
  allService,
  allServices,
  clientes,
  configErp,
  erp,
  handleCloseAutocomplete,
  handleForceSync,
  handleOpenAutocomplete,
  labelErp,
  loadingAutocomplete,
  navigate,
  openAutocomplete,
  openFilter,
  options,
  paramsSearchCliente,
  searchAutocomplete,
  searchTerm,
  selectStatus,
  selectedCliente,
  selectedHost,
  selectedService,
  services,
  setOpenFilter,
  setOpenModalCreate,
  setOpenModalCriarGrupo,
  setParamsSearchCliente,
  setSearchAutocomplete,
  setSearchTerm,
  setSelectStatus,
  setSelectedCliente,
  setSelectedHost,
  setSelectedService,
  showCanceled,
  startSyncAllowed,
  statusOptionsUnique,
  styles,
  syncRunning,
  theme,
  value,
  windowWith,
  statusFilter,
  setStatusFilter,
  selectedTagFilter,
  setSelectedTagFilter,
  availableTags,
  configStatusFilter,
  setConfigStatusFilter,
  tipoClienteFilter,
  setTipoClienteFilter,
}) {
  const [anchorElNovoCliente, setAnchorElNovoCliente] = useState(null);

  // Menu "Criar Grupo" x "Integração ERP" — apenas para administradores
  const showCriarGrupo = admPermissao;

  const handleIntegracaoErp = () => {
    setAnchorElNovoCliente(null);
    if (enableClientCreationModal) {
      setOpenModalCreate(true);
    } else {
      navigate("/interno/administracao/integracaoErp");
    }
  };

  const handleCriarGrupo = () => {
    setAnchorElNovoCliente(null);
    setOpenModalCriarGrupo(true);
  };

  const handleNovoCliente = (event) => {
    if (showCriarGrupo) {
      setAnchorElNovoCliente(event.currentTarget);
    } else {
      handleIntegracaoErp();
    }
  };

  const buildClientStorage = (clienteId) => ({
    interfaceId: "",
    clienteId,
    hostId: "",
    selectTime: 5,
    initialDate: "",
    finalDate: "",
  });

  const clearSelectedCliente = () => {
    localStorage.removeItem("filterData");
    setSelectedCliente(null);
    navigate("/interno/home/perfil/");
  };

  const handleSelectCliente = (newValue) => {
    setSelectedCliente(newValue);
    localStorage.setItem(
      "filterData",
      JSON.stringify(buildClientStorage(newValue?.id)),
    );
  };

  const configRefErp = (invertInfo) => {
    switch (erp) {
      case "HUBSOFT":
        return {
          labelRef: invertInfo ? "Serviço" : "Referência",
        };
      case "IXC":
        return {
          labelRef: invertInfo ? "Contrato" : "Descrição auxiliar",
        };
      default:
        return {
          labelRef: "",
        };
    }
  };

  const getServiceLabel = (option) => {
    if (typeof option === "string") return option;
    if (
      !option ||
      (!option?.id && !option?.id_cliente_servico && !option?.nome)
    ) {
      return "";
    }

    return `${option?.id_cliente_servico || option?.id || ""} - ${
      option?.nome ?? ""
    }`;
  };

  const normalizeService = (service) => {
    if (!service) return null;

    const serviceId = getServiceId(service);

    return {
      ...service,
      id: service?.id ?? serviceId,
      id_cliente_servico: service?.id_cliente_servico ?? serviceId,
    };
  };

  const handleServiceChange = (newValue) => {
    setSelectedService(normalizeService(newValue));
  };

  const filterServiceOptions = (options, params) => {
    const inputValue = params.inputValue.toLowerCase();

    return options.filter(
      (option) =>
        option.nome?.toLowerCase().includes(inputValue) ||
        option.id_cliente_servico?.toString().includes(params.inputValue) ||
        option.id?.toString().includes(params.inputValue) ||
        option.referencia?.toLowerCase().includes(inputValue) ||
        option.ownerNome?.toLowerCase().includes(inputValue),
    );
  };

  const renderServiceOption = (props, option) => {
    const { key, ...optionProps } = props;

    if (!option.referencia && !option.ownerNome) {
      return (
        <li key={key} {...optionProps}>
          {getServiceLabel(option)}
        </li>
      );
    }

    return (
      <li key={key} {...optionProps}>
        <Box sx={styles.boxLine}>
          <span style={{ fontWeight: "bold" }}>{getServiceLabel(option)}</span>
          {option.referencia?.trim() && (
            <>
              <Divider sx={{ margin: "2px 0" }} />
              <span style={{ fontSize: "0.85rem", opacity: 0.9 }}>
                <span style={{ fontWeight: "bold" }}>
                  {configRefErp(option?.invertInfo).labelRef}:
                </span>{" "}
                {option.referencia}
              </span>
            </>
          )}
          {option.ownerNome && (
            <>
              <Divider sx={{ margin: "2px 0" }} />
              <span style={{ fontSize: "0.85rem", opacity: 0.9 }}>
                <span style={{ fontWeight: "bold" }}>Cliente:</span>{" "}
                {option.ownerNome}
              </span>
            </>
          )}
        </Box>
      </li>
    );
  };

  const renderServiceOptionWithRefLabel = (props, option) => {
    const { key, ...optionProps } = props;

    if (!option.refLabel && !option.ownerNome) {
      return (
        <li key={key} {...optionProps}>
          {getServiceLabel(option)}
        </li>
      );
    }

    return (
      <li key={key} {...optionProps}>
        <Box sx={styles.boxLine}>
          <span style={{ fontWeight: "bold" }}>
            {option.refLabel
              ? `${option.refLabel?.id_cliente_servico} - ${option.refLabel?.nome}`
              : getServiceLabel(option)}
          </span>
          {option.refLabel?.referencia?.trim() && (
            <>
              <Divider sx={{ margin: "2px 0" }} />
              <span style={{ fontSize: "0.85rem", opacity: 0.9 }}>
                <span style={{ fontWeight: "bold" }}>
                  {configRefErp(option?.refLabel?.invertInfo).labelRef}:
                </span>{" "}
                {option.refLabel?.referencia}
              </span>
            </>
          )}
          {option.ownerNome && (
            <>
              <Divider sx={{ margin: "2px 0" }} />
              <span style={{ fontSize: "0.85rem", opacity: 0.9 }}>
                <span style={{ fontWeight: "bold" }}>Cliente:</span>{" "}
                {option.ownerNome}
              </span>
            </>
          )}
        </Box>
      </li>
    );
  };

  const serviceFilterConfigByTab = {
    0: {
      label: labelErp,
      value: selectedService,
      options: allService,
      onChange: (event, newValue) => {
        handleServiceChange(newValue);
      },
      renderOption: renderServiceOptionWithRefLabel,
    },
    1: {
      label: labelErp,
      value: selectedService,
      options: showCanceled
        ? allServices
        : allServices?.filter(
            (service) =>
              !String(service?.status || "")
                .toLowerCase()
                .includes("cancelado"),
          ),
      onChange: (event, newValue) => {
        handleServiceChange(newValue);
      },
    },
    2: {
      label: labelErp,
      value: selectedService,
      options: showCanceled
        ? services
        : services?.filter(
            (service) =>
              !String(service?.status || "")
                .toLowerCase()
                .includes("cancelado"),
          ),
      onChange: (event, newValue) => {
        handleServiceChange(newValue);
      },
    },
    3: {
      label: labelErp,
      value: selectedService,
      options: allServices,
      onChange: (event, newValue) => {
        handleServiceChange(newValue);
      },
    },
  };

  const activeServiceFilterConfig = serviceFilterConfigByTab[value];

  const actions = [
    {
      icon: <FilterAltOutlinedIcon />,
      name: "Filtro",
      onClick: () => setOpenFilter(true),
    },
    {
      icon: <PersonAddAlt1OutlinedIcon />,
      name: "Novo Cliente",
      onClick: (event) => handleNovoCliente(event),
    },
    ...(actionsPermission && value === 1 && selectedCliente
      ? [
          {
            icon: <Cached className={syncRunning ? "rotating" : ""} />,
            name: syncRunning
              ? "Sincronizando ONUs..."
              : "Sincronizar ONUs globais",
            onClick: handleForceSync,
          },
        ]
      : []),
  ];

  const selectorComponentLeft = (
    <Box sx={styles.boxFilters}>
      {(erp === "HUBSOFT" || erp === "IXC") && (
        <Box sx={{ minWidth: 120 }}>
          <FormControl variant="outlined" size="small" sx={styles.formMenu}>
            <InputLabel id="demo-simple-select-label">Buscar por</InputLabel>
            <Select
              label="Buscar por"
              value={paramsSearchCliente.typeSearch}
              onChange={(e) =>
                setParamsSearchCliente({
                  ...paramsSearchCliente,
                  typeSearch: e.target.value,
                  params: "",
                })
              }
            >
              <MenuItem value={1}>Cliente</MenuItem>
              <MenuItem value={2}>ID {configErp[erp]}</MenuItem>
              <MenuItem value={3}>CPF/CNPJ</MenuItem>
              {erp === "HUBSOFT" && <MenuItem value={4}>IPV4</MenuItem>}
              {erp === "HUBSOFT" && <MenuItem value={5}>Login</MenuItem>}
            </Select>
          </FormControl>
        </Box>
      )}
      {paramsSearchCliente.typeSearch === 1 && (
        <>
          <VirtualizedAutocomplete
            sx={{
              width: "100%",
              maxWidth: 500,
            }}
            value={selectedCliente}
            onChange={(_, newValue) => handleSelectCliente(newValue)}
            clearIcon={
              <Clear fontSize="small" onClick={clearSelectedCliente} />
            }
            size="small"
            options={clientes}
            getOptionLabel={(option) => getClienteOptionLabel(option)}
            renderOption={renderOpcaoCliente()}
            renderInput={(params) => (
              <CampoCliente
                params={params}
                option={selectedCliente}
                label="Cliente"
                sx={styles.filterAutocomplete}
              />
            )}
          />
          {!selectedCliente && (
            <FormControl
              variant="outlined"
              size="small"
              sx={{ minWidth: 200, ...styles.filterAutocomplete }}
            >
              <InputLabel id="status-filter-label">
                Filtro de Preenchimento
              </InputLabel>
              <Select
                labelId="status-filter-label"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Filtro de Preenchimento"
              >
                <MenuItem value="todos">Todos</MenuItem>
                <MenuItem value="preenchido">Preenchidos</MenuItem>
                <MenuItem value="pendente">Pendentes</MenuItem>
              </Select>
            </FormControl>
          )}
        </>
      )}
      {paramsSearchCliente.typeSearch === 2 && (
        <VirtualizedAutocomplete
          size="small"
          sx={{
            width: "100%",
            maxWidth: 500,
          }}
          open={openAutocomplete}
          onOpen={handleOpenAutocomplete}
          onClose={handleCloseAutocomplete}
          noOptionsText={
            searchAutocomplete?.trim() && options?.length === 0
              ? "Nenhum cliente encontrado"
              : !searchAutocomplete?.trim() &&
                `Digite o ID do ${configErp[erp]}`
          }
          value={selectedCliente}
          getOptionLabel={(option) => getClienteOptionLabel(option)}
          renderOption={(props, option) => {
            const { key, ...optionProps } = props;

            return (
              <li key={key} {...optionProps} data-conteudo-rico="true">
                <Box sx={styles.boxLine}>
                  <Box sx={styles.linhaNomeOpcao}>
                    <span style={{ fontWeight: "bold" }}>
                      {`${option.clienteErpId || ""} - ${getNomeCliente(option)}`}
                    </span>
                    <SeloCliente option={option} />
                  </Box>

                  {option.servico && (
                    <>
                      <Divider sx={{ margin: "2px 0" }} />
                      <span style={{ fontSize: "0.85rem", opacity: 0.9 }}>
                        <span style={{ fontWeight: "bold" }}>Serviço:</span>{" "}
                        {`${option.servico.id} - ${option.servico.descricao}`}
                      </span>
                    </>
                  )}
                </Box>
              </li>
            );
          }}
          clearIcon={<Clear fontSize="small" onClick={clearSelectedCliente} />}
          options={options}
          loading={loadingAutocomplete}
          onInputChange={(event, newInputValue) => {
            setSearchAutocomplete(newInputValue);
          }}
          onChange={(event, newValue) => handleSelectCliente(newValue)}
          filterOptions={(options, state) => {
            const inputValue = state.inputValue.toLowerCase()?.trim();
            return options.filter((option) => {
              const matchClienteId = option?.clienteErpId
                ?.toString()
                .includes(inputValue);
              const matchServicoId = option?.servico?.id
                ?.toString()
                .includes(inputValue);
              const matchNome = getNomeCliente(option)
                .toLowerCase()
                .includes(inputValue);

              return (
                matchClienteId || matchServicoId || matchNome
              );
            });
          }}
          renderInput={(params) => (
            <CampoCliente
              params={params}
              option={selectedCliente}
              sx={styles.filterAutocomplete}
              label={selectedCliente ? "Cliente" : `ID do ${configErp[erp]}`}
              adornoExtra={
                loadingAutocomplete ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null
              }
            />
          )}
        />
      )}
      {paramsSearchCliente.typeSearch === 3 && (
        <VirtualizedAutocomplete
          size="small"
          sx={{
            width: "100%",
            maxWidth: 500,
          }}
          open={openAutocomplete}
          onOpen={handleOpenAutocomplete}
          onClose={handleCloseAutocomplete}
          noOptionsText={
            searchAutocomplete?.trim() && options?.length === 0
              ? "Nenhum cliente encontrado"
              : !searchAutocomplete?.trim() && "Digite o CPF ou CNPJ do cliente"
          }
          value={selectedCliente}
          getOptionLabel={(option) => getClienteOptionLabel(option)}
          renderOption={(props, option) => {
            const { key, ...optionProps } = props;

            return (
              <li key={key} {...optionProps} data-conteudo-rico="true">
                <Box sx={styles.boxLine}>
                  <Box sx={styles.linhaNomeOpcao}>
                    <span style={{ fontWeight: "bold" }}>
                      {option.isGrupoEconomico
                        ? getNomeCliente(option)
                        : `${option.clienteErpId || ""} - ${getNomeCliente(option)}`}
                    </span>
                    <SeloCliente option={option} />
                  </Box>

                  {option.cnpj && (
                    <>
                      <Divider sx={{ margin: "2px 0" }} />
                      <span style={{ fontSize: "0.85rem", opacity: 0.9 }}>
                        <span style={{ fontWeight: "bold" }}>
                          {`${tipoDocumento(option.cnpj)}:`}
                        </span>{" "}
                        {maskCpfCnpj(option.cnpj)}
                      </span>
                    </>
                  )}
                </Box>
              </li>
            );
          }}
          clearIcon={<Clear fontSize="small" onClick={clearSelectedCliente} />}
          options={clientes}
          loading={loadingAutocomplete}
          onInputChange={(event, newInputValue) => {
            setSearchAutocomplete(newInputValue);
          }}
          onChange={(event, newValue) => handleSelectCliente(newValue)}
          filterOptions={(options, state) => {
            const inputValue = state.inputValue.toLowerCase().trim();

            if (!inputValue) return options;

            return options?.filter((option) => {
              const onlyNumbers = (value) =>
                String(value || "").replace(/\D/g, "");

              const inputNumbers = onlyNumbers(inputValue);
              const matchCnpj =
                inputNumbers !== "" &&
                onlyNumbers(option.cnpj).includes(inputNumbers);

              const matchClienteId = String(option.clienteErpId || "")
                .toLowerCase()
                .includes(inputValue);

              const matchNome = getNomeCliente(option)
                .toLowerCase()
                .includes(inputValue);

              return matchCnpj || matchClienteId || matchNome;
            });
          }}
          renderInput={(params) => (
            <CampoCliente
              params={params}
              option={selectedCliente}
              sx={styles.filterAutocomplete}
              label={selectedCliente ? "Cliente" : "CPF / CNPJ do cliente"}
              adornoExtra={
                loadingAutocomplete ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null
              }
            />
          )}
        />
      )}
      {paramsSearchCliente.typeSearch === 4 && erp === "HUBSOFT" && (
        <VirtualizedAutocomplete
          size="small"
          sx={{
            width: "100%",
            maxWidth: 500,
          }}
          open={openAutocomplete}
          onOpen={handleOpenAutocomplete}
          onClose={handleCloseAutocomplete}
          noOptionsText={
            searchAutocomplete?.trim() && options?.length === 0
              ? "Nenhum cliente encontrado"
              : !searchAutocomplete?.trim() && `Digite o IPV4 do cliente`
          }
          value={selectedCliente}
          getOptionLabel={getClienteErpOptionLabel}
          renderOption={(props, option) => {
            const { key, ...optionProps } = props;

            return (
              <li key={key} {...optionProps} data-conteudo-rico="true">
                <Box sx={styles.boxLine}>
                  <Box sx={styles.linhaNomeOpcao}>
                    <span style={{ fontWeight: "bold" }}>
                      {`${option.clienteErpId || ""} - ${getNomeCliente(option)}`}
                    </span>
                    <SeloCliente option={option} />
                  </Box>

                  {option.ip && (
                    <>
                      <Divider sx={{ margin: "2px 0" }} />
                      <span style={{ fontSize: "0.85rem", opacity: 0.9 }}>
                        <span style={{ fontWeight: "bold" }}>IPV4:</span>{" "}
                        {`${option.ip} `}
                      </span>
                    </>
                  )}
                </Box>
              </li>
            );
          }}
          clearIcon={<Clear fontSize="small" onClick={clearSelectedCliente} />}
          options={options}
          loading={loadingAutocomplete}
          onInputChange={(event, newInputValue) => {
            setSearchAutocomplete(newInputValue);
          }}
          onChange={(event, newValue) => handleSelectCliente(newValue)}
          filterOptions={(options, state) => {
            const inputValue = state.inputValue.toLowerCase()?.trim();
            return options.filter((option) => {
              const matchClienteId = option?.clienteErpId
                ?.toString()
                .includes(inputValue);
              const matchServicoId = option?.servico?.id
                ?.toString()
                .includes(inputValue);
              const matchNome = getNomeCliente(option)
                .toLowerCase()
                .includes(inputValue);
              const matchIp = option?.ip?.toLowerCase().includes(inputValue);

              return (
                matchClienteId ||
                matchServicoId ||
                matchNome ||
                matchIp
              );
            });
          }}
          renderInput={(params) => (
            <CampoCliente
              params={params}
              option={selectedCliente}
              rotuloDaOpcao={getClienteErpOptionLabel}
              sx={styles.filterAutocomplete}
              label={selectedCliente ? "Cliente" : `IPV4 do cliente`}
              adornoExtra={
                loadingAutocomplete ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null
              }
            />
          )}
        />
      )}
      {paramsSearchCliente.typeSearch === 5 && erp === "HUBSOFT" && (
        <VirtualizedAutocomplete
          size="small"
          sx={{
            width: "100%",
            maxWidth: 500,
          }}
          open={openAutocomplete}
          onOpen={handleOpenAutocomplete}
          onClose={handleCloseAutocomplete}
          noOptionsText={
            searchAutocomplete?.trim() && options?.length === 0
              ? "Nenhum cliente encontrado"
              : !searchAutocomplete?.trim() && `Digite o login do cliente`
          }
          value={selectedCliente}
          getOptionLabel={getClienteErpOptionLabel}
          renderOption={(props, option) => {
            const { key, ...optionProps } = props;

            return (
              <li key={key} {...optionProps} data-conteudo-rico="true">
                <Box sx={styles.boxLine}>
                  <Box sx={styles.linhaNomeOpcao}>
                    <span style={{ fontWeight: "bold" }}>
                      {`${option.clienteErpId || ""} - ${getNomeCliente(option)}`}
                    </span>
                    <SeloCliente option={option} />
                  </Box>

                  {option.login && (
                    <>
                      <Divider sx={{ margin: "2px 0" }} />
                      <span style={{ fontSize: "0.85rem", opacity: 0.9 }}>
                        <span style={{ fontWeight: "bold" }}>Login:</span>{" "}
                        {`${option.login}`}
                      </span>
                    </>
                  )}
                </Box>
              </li>
            );
          }}
          clearIcon={<Clear fontSize="small" onClick={clearSelectedCliente} />}
          options={options}
          loading={loadingAutocomplete}
          onInputChange={(event, newInputValue) => {
            setSearchAutocomplete(newInputValue);
          }}
          onChange={(event, newValue) => handleSelectCliente(newValue)}
          filterOptions={(options, state) => {
            const inputValue = state.inputValue.toLowerCase()?.trim();

            return options.filter((option) => {
              const matchClienteId = option?.clienteErpId
                ?.toString()
                .includes(inputValue);
              const matchNome = getNomeCliente(option)
                .toLowerCase()
                .includes(inputValue);
              const matchLogin = option?.login
                ?.toLowerCase()
                .includes(inputValue);

              return matchClienteId || matchNome || matchLogin;
            });
          }}
          renderInput={(params) => (
            <CampoCliente
              params={params}
              option={selectedCliente}
              rotuloDaOpcao={getClienteErpOptionLabel}
              sx={styles.filterAutocomplete}
              label={selectedCliente ? "Cliente" : `Login do cliente`}
              adornoExtra={
                loadingAutocomplete ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null
              }
            />
          )}
        />
      )}

      {!selectedCliente && (
        <TipoClienteFilter
          value={tipoClienteFilter}
          onChange={setTipoClienteFilter}
          id="filtro-tipo-cliente-lista"
          sx={{ width: "100%", maxWidth: 230 }}
        />
      )}

      {!selectedCliente && (
        <Autocomplete
          options={availableTags || []}
          getOptionLabel={(option) => option.tagName || ""}
          value={selectedTagFilter}
          onChange={(e, newValue) => setSelectedTagFilter(newValue)}
          size="small"
          sx={{ width: "100%", maxWidth: 250 }}
          renderInput={(params) => (
            <TextField
              {...params}
              sx={styles.filterAutocomplete}
              label="Filtrar por tag"
              variant="outlined"
            />
          )}
        />
      )}

      {selectedCliente && activeServiceFilterConfig && (
        <Box sx={styles.boxFilters}>
          <ServiceFilter
            styles={styles}
            getServiceLabel={getServiceLabel}
            filterOptions={filterServiceOptions}
            renderOption={renderServiceOption}
            {...activeServiceFilterConfig}
          />

          {(value === 1 || value === 2) && (
            <FormControl
              variant="outlined"
              size="small"
              sx={styles.configStatusSelect}
            >
              <InputLabel id="config-status-filter-label">
                Configuração
              </InputLabel>
              <Select
                labelId="config-status-filter-label"
                value={configStatusFilter || ""}
                onChange={(e) => setConfigStatusFilter(e.target.value)}
                label="Configuração"
                renderValue={(selected) => {
                  if (!selected) return "";
                  if (selected === "configurado") {
                    return (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          height: "100%",
                        }}
                      >
                        <Chip
                          label="CONFIGURADO"
                          size="small"
                          sx={styles.configStatusChipSuccess}
                        />
                      </Box>
                    );
                  }
                  if (selected === "nao_configurado") {
                    return (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          height: "100%",
                        }}
                      >
                        <Chip
                          label="NÃO CONFIGURADO"
                          size="small"
                          sx={styles.configStatusChipGray}
                        />
                      </Box>
                    );
                  }
                  return selected;
                }}
                endAdornment={
                  configStatusFilter ? (
                    <InputAdornment position="end" sx={{ mr: 2 }}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfigStatusFilter("");
                        }}
                      >
                        <Clear fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }
              >
                <MenuItem value="configurado">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Chip
                      label="CONFIGURADO"
                      size="small"
                      sx={styles.configStatusChipSuccess}
                    />
                  </Box>
                </MenuItem>
                <MenuItem value="nao_configurado">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Chip
                      label="NÃO CONFIGURADO"
                      size="small"
                      sx={styles.configStatusChipGray}
                    />
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          )}

          {value === 2 && (
            <TextField
              size="small"
              fullWidth
              variant="outlined"
              placeholder="Endereço, status, etc..."
              label="Busca Livre"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ ...styles.searchField }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setSearchTerm("")} edge="end">
                      <Clear size={"small"} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          )}

          {value === 0 && (
            <Autocomplete
              size="small"
              value={selectStatus}
              options={statusOptionsUnique || []}
              getOptionLabel={(option) => {
                if (!option || !option?.status) return "";
                return option?.status;
              }}
              isOptionEqualToValue={(option, value) =>
                option?.status === value?.status
              }
              sx={styles.searchField}
              onChange={(event, newValue) => {
                setSelectStatus(newValue);
              }}
              renderInput={(params) => <TextField {...params} label="Status" />}
            />
          )}

          {value === 3 && (
            <Autocomplete
              disabled={!allHostsCliente || !selectedService}
              size="small"
              sx={styles.searchField}
              options={allHostsCliente}
              value={selectedHost}
              getOptionLabel={(option) => option?.host || ""}
              onChange={(event, value) => setSelectedHost(value)}
              renderInput={(params) => <TextField {...params} label="Host" />}
            />
          )}
        </Box>
      )}
    </Box>
  );

  const selectorComponentRight = (
    <Box sx={{ display: "flex", gap: 1, alignItems: "stretch" }}>
      {startSyncAllowed && (
        <Tooltip
          title="Sincronizar ONUs com o ERP (todos os clientes) — A edição de hosts ficará indisponível durante a execução"
          arrow
          placement="bottom"
        >
          <Button
            variant="outlined"
            onClick={handleForceSync}
            disabled={syncRunning}
            sx={{
              color: theme.palette.text.main,
              borderColor: theme.palette.text.main,

              "& .rotating": { animation: "spin 1s linear infinite" },
              "@keyframes spin": {
                "0%": { transform: "rotate(0deg)" },
                "100%": { transform: "rotate(-360deg)" },
              },
            }}
          >
            <Cached
              fontSize="small"
              className={syncRunning ? "rotating" : ""}
            />
          </Button>
        </Tooltip>
      )}
      <Button
        variant="outlined"
        startIcon={<Add />}
        onClick={(event) => handleNovoCliente(event)}
        sx={{
          color: theme.palette.text.main,
          borderColor: theme.palette.text.main,
          minWidth: "150px",
        }}
      >
        Novo Cliente
      </Button>
    </Box>
  );

  return (
    <>
      <TopBarFilter
        actions={actions}
        selectorComponentRight={selectorComponentRight}
        selectorComponentLeft={selectorComponentLeft}
        windowWidth={windowWith}
        openFilter={openFilter}
        setOpenFilter={setOpenFilter}
      />
      <Menu
        anchorEl={anchorElNovoCliente}
        open={Boolean(anchorElNovoCliente)}
        onClose={() => setAnchorElNovoCliente(null)}
      >
        <MenuItem onClick={handleCriarGrupo}>
          <WorkspacesOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
          Criar Grupo
        </MenuItem>
        <MenuItem onClick={handleIntegracaoErp}>
          <CloudSyncOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
          Integração ERP
        </MenuItem>
      </Menu>
    </>
  );
}
